import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define the structure of a Theme
export interface HomepageSection {
    id: string;
    type: 'hero' | 'products-grid' | 'features' | 'testimonials' | 'newsletter';
    settings: Record<string, any>;
}

export interface Theme {
    id: string;
    name: string;
    layout: 'classic' | 'modern' | 'luxury';
    colors: {
        background: string;
        foreground: string;
        primary: string;
        secondary: string;
        accent: string;
        border: string;
        muted: string;
        card: string;
        cardForeground: string;
    };
    typography: {
        fontFamily: string;
        headingFont: string;
        googleFontUrl: string;
    };
    borderRadius: string;
    shadows: {
        sm: string;
        md: string;
        lg: string;
    };
    homepage: {
        hero: {
            enabled: boolean;
            imageUrl: string;
            title: string;
            subtitle: string;
            buttonText: string;
            buttonLink: string;
            overlayOpacity: number;
        };
        sections: HomepageSection[];
    };
}

// Initial Themes based on the reference
export const initialThemes: Theme[] = [
    {
        id: 'classic-light',
        name: 'Classic Light (Default)',
        layout: 'classic',
        colors: {
            background: '#ffffff',
            foreground: '#1e293b', // Slate 800
            primary: '#16a34a', // Green (matches original)
            secondary: '#f1f5f9',
            accent: '#15803d', // Darker green
            border: '#e2e8f0',
            muted: '#64748b',
            card: '#ffffff',
            cardForeground: '#1e293b'
        },
        typography: {
            fontFamily: '"Cairo", sans-serif',
            headingFont: '"Cairo", sans-serif',
            googleFontUrl: 'https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;700&display=swap'
        },
        borderRadius: '0.75rem', // Matches --radius: 0.75rem
        shadows: {
            sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
            md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
            lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
        },
        homepage: {
            hero: {
                enabled: true,
                imageUrl: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?q=80&w=1988&auto=format&fit=crop',
                title: 'مرحباً بك في متجرنا',
                subtitle: 'أفضل المنتجات بأفضل الأسعار',
                buttonText: 'تسوق الآن',
                buttonLink: '/products',
                overlayOpacity: 0.2
            },
            sections: [
                { id: 'sec-1', type: 'features', settings: {} },
                { id: 'sec-2', type: 'products-grid', settings: { title: 'منتجات مميزة', count: 4 } }
            ]
        }
    },
    {
        id: 'midnight-gold',
        name: 'Midnight Gold',
        layout: 'luxury',
        colors: {
            background: '#0a0a0a',
            foreground: '#efcd98',
            primary: '#d4af37', // True Gold
            secondary: '#1a1a1a',
            accent: '#516740',
            border: '#333333',
            muted: '#a1a1aa',
            card: '#121212',
            cardForeground: '#efcd98'
        },
        typography: {
            fontFamily: '"Lato", sans-serif',
            headingFont: '"Playfair Display", serif',
            googleFontUrl: 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700&family=Playfair+Display:wght@400;700&display=swap'
        },
        borderRadius: '0rem',
        shadows: {
            sm: '0 1px 2px 0 rgb(0 0 0 / 0.5)',
            md: '0 4px 6px -1px rgb(0 0 0 / 0.5)',
            lg: '0 10px 15px -3px rgb(212 175 55 / 0.1)' // Gold glow
        },
        homepage: {
            hero: {
                enabled: true,
                imageUrl: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=2070&auto=format&fit=crop',
                title: 'الفخامة في كل تفصيل',
                subtitle: 'تشكيلة حصرية من أرقى المنتجات',
                buttonText: 'اكتشف المجموعة',
                buttonLink: '/collections/luxury',
                overlayOpacity: 0.5
            },
            sections: [
                { id: 'sec-1', type: 'products-grid', settings: { title: 'الأكثر مبيعاً', count: 8 } }
            ]
        }
    },
    {
        id: 'soft-cream',
        name: 'Soft Cream',
        layout: 'modern',
        colors: {
            background: '#fbf7f0',
            foreground: '#4a4a4a',
            primary: '#db1215',
            secondary: '#ffffff',
            accent: '#db1215',
            border: '#e6e6e6',
            muted: '#9ca3af',
            card: '#ffffff',
            cardForeground: '#4a4a4a'
        },
        typography: {
            fontFamily: '"Cairo", sans-serif',
            headingFont: '"Cairo", sans-serif',
            googleFontUrl: 'https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;700&display=swap'
        },
        borderRadius: '1rem',
        shadows: {
            sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
            md: '0 4px 6px -1px rgb(0 0 0 / 0.05)',
            lg: '0 20px 25px -5px rgb(0 0 0 / 0.05)'
        },
        homepage: {
            hero: {
                enabled: true,
                imageUrl: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?q=80&w=2070&auto=format&fit=crop',
                title: 'جمال الطبيعة',
                subtitle: 'منتجات عضوية 100%',
                buttonText: 'تصفح المنتجات',
                buttonLink: '/collections/organic',
                overlayOpacity: 0.1
            },
            sections: [
                { id: 'sec-1', type: 'features', settings: {} },
                { id: 'sec-2', type: 'newsletter', settings: {} }
            ]
        }
    }
];

interface ThemeContextType {
    activeTheme: Theme;
    setActiveTheme: (themeId: string) => void;
    updateTheme: (themeId: string, updates: Partial<Theme>) => void;
    availableThemes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
    // Default to the first theme
    const [activeTheme, setActiveThemeState] = useState<Theme>(initialThemes[0]);
    const [availableThemes, setAvailableThemes] = useState<Theme[]>(initialThemes);

    // Load theme from local storage on mount
    useEffect(() => {
        const savedThemeId = localStorage.getItem('activeThemeId');
        if (savedThemeId) {
            const theme = availableThemes.find(t => t.id === savedThemeId);
            if (theme) {
                setActiveThemeState(theme);
            }
        }
    }, []);

    // Apply theme variables to CSS root and inject fonts
    useEffect(() => {
        const root = document.documentElement;

        // Colors
        root.style.setProperty('--background', activeTheme.colors.background);
        root.style.setProperty('--foreground', activeTheme.colors.foreground);
        root.style.setProperty('--primary', activeTheme.colors.primary);
        root.style.setProperty('--secondary', activeTheme.colors.secondary);
        root.style.setProperty('--accent', activeTheme.colors.accent);
        root.style.setProperty('--border', activeTheme.colors.border);
        root.style.setProperty('--muted', activeTheme.colors.muted);
        root.style.setProperty('--card', activeTheme.colors.card);
        root.style.setProperty('--card-foreground', activeTheme.colors.cardForeground);

        // Typography
        root.style.setProperty('--font-sans', activeTheme.typography.fontFamily);
        root.style.setProperty('--font-heading', activeTheme.typography.headingFont);

        // Border Radius
        root.style.setProperty('--radius', activeTheme.borderRadius);

        // Shadows
        root.style.setProperty('--shadow-sm', activeTheme.shadows.sm);
        root.style.setProperty('--shadow-md', activeTheme.shadows.md);
        root.style.setProperty('--shadow-lg', activeTheme.shadows.lg);

        // Inject Font
        const fontId = 'dynamic-theme-font';
        let link = document.getElementById(fontId) as HTMLLinkElement;
        if (!link) {
            link = document.createElement('link');
            link.id = fontId;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }
        link.href = activeTheme.typography.googleFontUrl;

        // Set Layout Class
        document.body.className = document.body.className.replace(/theme-\w+/g, '').trim();
        document.body.classList.add(`theme-${activeTheme.layout}`);

    }, [activeTheme]);

    const setActiveTheme = (themeId: string) => {
        const theme = availableThemes.find(t => t.id === themeId);
        if (theme) {
            setActiveThemeState(theme);
            localStorage.setItem('activeThemeId', themeId);
        }
    };

    const updateTheme = (themeId: string, updates: Partial<Theme>) => {
        setAvailableThemes(prev => prev.map(theme => {
            if (theme.id === themeId) {
                const updatedTheme = { ...theme, ...updates };
                if (theme.id === activeTheme.id) {
                    setActiveThemeState(updatedTheme);
                }
                return updatedTheme;
            }
            return theme;
        }));
    };

    return (
        <ThemeContext.Provider value={{ activeTheme, setActiveTheme, updateTheme, availableThemes }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
